/**
 * Estado del RECORRIDO GUIADO del demo estático ("DEMO OPUS ai").
 *
 * El demo ya no es una foto terminada: el visitante ejecuta el flujo completo
 * (crear proyecto → subir PDF → resumen → chat → contexto → hoja → catálogos →
 * equivalentes → análisis con IA → exportar) y las pantallas REALES responden con
 * fixtures que dependen de la etapa. Este store es la única fuente de verdad
 * de en qué etapa va el visitante.
 *
 * Se persiste en sessionStorage para sobrevivir un F5 a mitad del recorrido.
 * Solo existe en modo demo (VITE_DEMO=1); en el build normal nada lo importa
 * con efecto (queda tree-shakeado).
 */
import { create } from 'zustand';

/** Etapas del recorrido, en orden. */
export const TOUR = {
  CREAR: 0,       // proyectos vacío → glow "Crear primer proyecto"
  SUBIR: 1,       // proyecto creado, sin documentos → glow zona de subida
  RESUMEN: 2,     // PDF leído e indexado → glow "Generar resumen"
  CHAT: 3,        // resumen listo → glow pestaña de preguntas
  CONTEXTO: 4,    // chat respondido → glow "Contexto de proyecto"
  HOJA: 5,        // contexto guardado → glow "Armar presupuesto"
  ARMAR: 6,       // borrador creado → glow paso 1 (armar la hoja)
  CATALOGOS: 7,   // hoja armada sin precios → glow catálogos de referencia
  EQUIVALENTES: 8,// ECOSTOS elegido → glow paso 2 (buscar equivalentes)
  SINTETICOS: 9,  // 29 con precio, 6 sin equivalente → glow paso 3
  VER: 10,        // todo con precio → abrir un precio hecho con IA + "Cómo lo pensó"
  FIN: 11,        // recorrido completo → se explica cómo importarlo en OPUS 27
} as const;

export interface TourState {
  stage: number;
  /** Etiqueta del proceso en curso (el asistente narra mientras corre). */
  busy: string | null;
  /** Cuántos conceptos llevan precio durante la animación de equivalentes. */
  pricedTick: number;
  /** Cuántos análisis con IA llevan matriz durante la animación del paso 3. */
  apuTick: number;
  /** Catálogos elegidos por el visitante en el paso de catálogos. */
  catalogIds: string[];
  /** Nombre que el visitante le puso a su proyecto. */
  projectName: string | null;
  setStage: (n: number) => void;
  setBusy: (label: string | null) => void;
  setTicks: (p: Partial<Pick<TourState, 'pricedTick' | 'apuTick'>>) => void;
  setCatalogIds: (ids: string[]) => void;
  setProjectName: (n: string) => void;
  reset: () => void;
}

const SS_KEY = 'demo-tour-v1';

function load(): Pick<TourState, 'stage' | 'catalogIds' | 'projectName'> {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as { stage?: number; catalogIds?: string[]; projectName?: string | null };
      return {
        stage: typeof p.stage === 'number' ? p.stage : TOUR.CREAR,
        catalogIds: Array.isArray(p.catalogIds) ? p.catalogIds : [],
        projectName: p.projectName ?? null,
      };
    }
  } catch { /* sessionStorage puede no existir (file:// raro) */ }
  return { stage: TOUR.CREAR, catalogIds: [], projectName: null };
}

function save(s: TourState) {
  try {
    sessionStorage.setItem(SS_KEY, JSON.stringify({ stage: s.stage, catalogIds: s.catalogIds, projectName: s.projectName }));
  } catch { /* noop */ }
}

export const useTourStore = create<TourState>((set, get) => ({
  ...load(),
  busy: null,
  pricedTick: 0,
  apuTick: 0,
  setStage: (n) => { set({ stage: n }); save(get()); },
  setBusy: (label) => set({ busy: label }),
  setTicks: (p) => set(p),
  setCatalogIds: (ids) => { set({ catalogIds: ids }); save(get()); },
  setProjectName: (n) => { set({ projectName: n }); save(get()); },
  reset: () => {
    set({ stage: TOUR.CREAR, busy: null, pricedTick: 0, apuTick: 0, catalogIds: [], projectName: null });
    save(get());
  },
}));

/** Acceso sin hook (para el resolver de fixtures y los schedulers). */
export const tour = {
  get stage() { return useTourStore.getState().stage; },
  setStage: (n: number) => useTourStore.getState().setStage(n),
  setBusy: (l: string | null) => useTourStore.getState().setBusy(l),
  setTicks: (p: Partial<Pick<TourState, 'pricedTick' | 'apuTick'>>) => useTourStore.getState().setTicks(p),
  get pricedTick() { return useTourStore.getState().pricedTick; },
  get apuTick() { return useTourStore.getState().apuTick; },
  get catalogIds() { return useTourStore.getState().catalogIds; },
  setCatalogIds: (ids: string[]) => useTourStore.getState().setCatalogIds(ids),
  get projectName() { return useTourStore.getState().projectName; },
  setProjectName: (n: string) => useTourStore.getState().setProjectName(n),
};
